import clsx from 'clsx'
import { rem } from 'polished'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import useMountedEffect from 'hooks/useMountedEffect'
import { groupBy, sortBy, take } from 'lib/array/arrayUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'

import type {
  AccommodationDayPickerSize,
  AccommodationDayView,
  AccommodationMobileDayCellSelectHandler,
} from './AccommodationCalendar.types'
import AccommodationCalendarDayMonth from './AccommodationCalendarDayMonth'
import type { AccommodationDayCellVariant } from './AccommodationCalendarLargeDayCell'

interface AccommodationMonthView {
  year: number
  month: number
  date: Date
  days: Array<AccommodationDayView>
}

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);

  > .full-width {
    grid-column: 1 / -1;
  }

  ${mediaHoverable} {
    &.enable-hover {
      > .type-check-in ~ .type-none:has(~ .type-none:hover), > .type-check-in ~ .type-none:hover {
        background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      }
    }
  }
`

const Gap20 = styled.div`
  height: ${rem(20)};
  grid-column: 1 / -1;
`

interface Props {
  views: Array<AccommodationDayView>
  size: AccommodationDayPickerSize
  className?: string
  onBackToMonth?: () => void
  initialYear?: number
  initialMonth?: number
  contentRef: React.RefObject<HTMLElement | null>
  startDate?: string
  endDate?: string
  onDaySelect?: AccommodationMobileDayCellSelectHandler
  availabilityVariantMap?: Map<string, AccommodationDayCellVariant>
  /**
   * When true, renders all months from today up to the initial month (instead
   * of only 3 around it). Required for flows where the user needs to scroll
   * freely from today to a specific future month.
   */
  renderFromStart?: boolean
}

function AccommodationCalendarDayPicker(props: Props) {
  const {
    views,
    className,
    initialYear,
    initialMonth,
    contentRef,
    onBackToMonth,
    startDate,
    endDate,
    onDaySelect,
    size,
    availabilityVariantMap,
    renderFromStart,
  } = props

  // always show month selected, previous month and next month
  const viewsByMonth = useMemo(() => {
    return sortBy(
      groupBy(views, view => `${view.year}-${view.month}`)
        .values()
        .map((viewGroup): AccommodationMonthView => ({
          date: new Date(viewGroup[0].date),
          month: viewGroup[0].month,
          year: viewGroup[0].year,
          days: viewGroup,
        })),
      group => group.date,
      'asc',
    )
  }, [views])

  const initialIndex = useMemo(() => {
    if (initialYear !== undefined && initialMonth !== undefined) {
      return viewsByMonth.findIndex(view => view.month === initialMonth && view.year === initialYear)
    }
    return -1
  }, [viewsByMonth, initialYear, initialMonth])

  const [monthsOnDisplay, setMonthsOnDisplay] = useState(() => {
    if (initialIndex >= 0) {
      if (renderFromStart) {
        return { toShow: initialIndex + 3, offset: 0 }
      }
      return { toShow: 3, offset: Math.max(initialIndex - 1, 0) }
    }
    return { toShow: 3, offset: 0 }
  })

  const [previousRef, previousInView] = useInView({ root: contentRef.current, rootMargin: '20px 0px 0px 0px' })
  const [nextRef, nextInView] = useInView({ root: contentRef.current, rootMargin: '0px 0px 100px 0px' })
  const monthElements = useRef<Record<string, HTMLDivElement | null>>({})
  const scrollRestoreMonth = useRef<typeof monthsOnDisplay | null>(null)

  useLayoutEffect(() => {
    if (initialIndex >= 0) {
      if (renderFromStart) {
        const month = viewsByMonth[initialIndex]
        const element = monthElements.current[+month.date]
        if (element) {
          element.scrollIntoView({ behavior: 'auto' })
        }
      } else if (monthsOnDisplay.offset > 0) {
        const month = viewsByMonth[monthsOnDisplay.offset + 1]
        const element = monthElements.current[+month.date]
        if (element) {
          element.scrollIntoView({ behavior: 'auto' })
        }
      }
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    if (previousInView) {
      // user scrolled to the top, so we show another month at the start
      scrollRestoreMonth.current = monthsOnDisplay
      setMonthsOnDisplay({ toShow: monthsOnDisplay.toShow + 1, offset: monthsOnDisplay.offset - 1 })
    }
  }, [previousInView])

  useLayoutEffect(() => {
    // this handles when we scroll up and a new month appears
    // we want the user to not be at the top, but stay in the 'same position' before
    // the new month came in
    if (scrollRestoreMonth.current !== undefined) {
      const month = viewsByMonth[monthsOnDisplay.offset + 1]
      const element = monthElements.current[+month.date]
      if (element) {
        contentRef.current?.scrollTo({
          behavior: 'auto',
          top: element.offsetHeight + 40,
        })
      }
      scrollRestoreMonth.current = null
    }
  }, [monthsOnDisplay.offset]) // oxlint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (nextInView) {
      // scrolled to the bottom of our current set of months - add one more month to the bottom
      setMonthsOnDisplay(monthsOnDisplay => ({ ...monthsOnDisplay, toShow: monthsOnDisplay.toShow + 1 }))
    }
  }, [nextInView])

  return (<VerticalSpacer className={className} gap={20}>
    {monthsOnDisplay.offset > 0 && <div ref={previousRef} />}
    {/*
      We use a single grid container for every calendar and their content as this allows
      us to apply styles (such as hover) via css sibling selectors across months
      muuuch more performant than trying to do it in javascript
      It does make the styling a little more fragile, so be aware of this.
    */}
    <CalendarGrid className={clsx({ 'enable-hover': !endDate })}>
      {take(viewsByMonth, monthsOnDisplay.toShow, monthsOnDisplay.offset).map((view) => {
        return <React.Fragment key={+view.date}>
          {size === 'small' && <VerticalSpacer
            gap={4}
            className="full-width"
            ref={(el) => { monthElements.current[+view.date] = el }}
          >
            <Group direction="horizontal" horizontalAlign="space-between">
              <Heading variant="heading5">
                <FormatDateTime value={view.date} format="long-month" />
              </Heading>
              {onBackToMonth && <TextLink size="large" onClick={onBackToMonth}>{view.year}</TextLink>}
              {!onBackToMonth && <BodyText variant="large" weight="bold">{view.year}</BodyText>}
            </Group>
          </VerticalSpacer>}
          {size === 'large' && <Heading
            variant="heading5"
            className="full-width"
            ref={(el) => { monthElements.current[+view.date] = el }}
          >
            <FormatDateTime value={view.date} format="long-month-long-year" />
          </Heading>}
          <Gap20 />
          <AccommodationCalendarDayMonth
            days={view.days}
            startDate={startDate}
            endDate={endDate}
            onDaySelect={onDaySelect}
            size={size}
            availabilityVariantMap={availabilityVariantMap}
          />
          <Gap20 />
        </React.Fragment>
      })}
    </CalendarGrid>
    {(monthsOnDisplay.toShow + monthsOnDisplay.offset) < views.length && <div ref={nextRef} />}
  </VerticalSpacer>)
}

export default React.memo(AccommodationCalendarDayPicker)
